import {
  FieldHelperProps,
  FieldHookConfig,
  FieldInputProps,
  FieldMetaProps,
  FormikContextType,
  FormikHelpers,
  useField,
  useFormikContext,
} from 'formik';
import {keys} from 'lodash';
import {useMemo, SetStateAction} from 'react';
import * as Sentry from '@sentry/react';
import {dotize} from '../../utils/flattenObject';
import {UPDATES, ReccFormUpdateKey, ReccFormUpdateValue} from '../../shared/UPDATES';

import {getEnvironment} from '../../utils/envTest';

// Note this is a .ts file and not a .tsx file because .tsx generics
// don't work properly with this kind of function wrapping

const keyToDebugOn: string | null = null;

const formLogger = (
  sourceKey: string,
  field: string,
  value: unknown,
  shouldValidate?: boolean
) => {
  if (sourceKey) {
    Sentry.addBreadcrumb({
      message: `[FORM_UPDATE ${sourceKey}] ${field} =`,
      data: {
        value,
        sourceKey,
        field,
      },
    });
    if (getEnvironment() !== 'PROD') {
      console.log(
        `[FORM_UPDATE ${sourceKey}] ${field} =`,
        value,
        `shouldValidate=${shouldValidate}`
      );
    }
  }
};

export type SetFieldValueWithSourceKey<Values> = (
  sourceKey: ReccFormUpdateValue,
  field: string,
  value: any,
  shouldValidate?: boolean
) => ReturnType<FormikContextType<Values>['setFieldValue']>;

export type SetValuesWithSourceKey<Values> = (
  sourceKey: ReccFormUpdateValue,
  values: SetStateAction<Values>,
  shouldValidate?: boolean
) => ReturnType<FormikContextType<Values>['setValues']>;

type AutoFormContextType<Values> = Omit<
  FormikContextType<Values>,
  'setFieldValue' | 'setValues'
> & {
  setFieldValue: SetFieldValueWithSourceKey<Values>;
  setValues: SetValuesWithSourceKey<Values>;
};

export type AutoFieldHelpers<Val> = Omit<FieldHelperProps<Val>, 'setValue'> & {
  setValue: (
    sourceKey: ReccFormUpdateValue,
    value: Val,
    shouldValidate?: boolean
  ) => ReturnType<FieldHelperProps<Val>['setValue']>;
};

export const useAutoField = <Val>(
  key: string | FieldHookConfig<Val>
): [FieldInputProps<Val>, FieldMetaProps<Val>, AutoFieldHelpers<Val>] => {
  const [input, meta, helpersUnchanged] = useField<Val>(key);
  const helpers: AutoFieldHelpers<Val> = useMemo(() => {
    const setValue = (
      sourceKey: ReccFormUpdateValue,
      value: Val,
      shouldValidate?: boolean | undefined
    ) => {
      formLogger(sourceKey, key.toString(), value, shouldValidate);
      if (keyToDebugOn && process.env.NODE_ENV === 'development') {
        if (
          key.toString().includes(keyToDebugOn) ||
          (typeof value === 'object' && keys(value).join(',').includes(keyToDebugOn)) ||
          keys(dotize.convert(value)).includes(keyToDebugOn)
        ) {
          debugger;
        }
      }
      return helpersUnchanged.setValue(value, shouldValidate);
    };
    return {...helpersUnchanged, setValue};
  }, [helpersUnchanged]);
  const returnVal = useMemo(() => {
    return [input, meta, helpers] as [
      // horrible typescript
      FieldInputProps<Val>,
      FieldMetaProps<Val>,
      AutoFieldHelpers<Val>
    ];
  }, [input, meta, helpers]);

  return returnVal;
};

export const useAutoForm = <Values = object>(): AutoFormContextType<Values> => {
  const returnVal = useFormikContext<Values>();

  const checkDebugConditions = (field: string | undefined, value: any) => {
    if (returnVal.values && keyToDebugOn && process.env.NODE_ENV === 'development') {
      if ((returnVal.values as any).debug) {
        debugger;
      }
      //
      // Uncomment to get a debugger when a certain value is to something
      if (
        (field && field.includes(keyToDebugOn)) ||
        keys(value).join(',').includes(keyToDebugOn) ||
        keys(dotize.convert(value)).includes(keyToDebugOn)
      ) {
        debugger;
      }
    }
  };

  const setFieldValue: SetFieldValueWithSourceKey<Values> = (
    sourceKey,
    field,
    value,
    shouldValidate
  ) => {
    formLogger(sourceKey, field, value);
    checkDebugConditions(field, value);
    return returnVal.setFieldValue(field, value, shouldValidate);
  };

  const setValues: SetValuesWithSourceKey<Values> = (
    sourceKey,
    values,
    shouldValidate
  ) => {
    formLogger(sourceKey, 'setValues', values, shouldValidate);
    checkDebugConditions(undefined, values);
    return returnVal.setValues(values, shouldValidate);
  };

  return {
    ...returnVal,
    setFieldValue,
    setValues,
  };
};

export const useLoadedAutoField = () => {
  const [input, meta, helpersUnchanged] = useField<boolean | undefined>('loaded');
  return {
    loaded: input.value,
    setLoaded: () => {
      setTimeout(() => {
        helpersUnchanged.setValue(true);
      }, 1000);
    },
  };
};
